import React from 'react';
import { connect } from 'react-redux';
import { setDeveloperName, setContentType, setTypeElementId, setContentFormat, setIsFixed, setAccess, setIsVersionless,
    setDeveloperSummary } from '../../../../../../actions/reduxActions/valueEditor';
import { Modal } from 'react-bootstrap';
import GenericFormField from '../../../../../generic/GenericFormField';
import { ELEMENT_LIST_VIEW, VALUE_TYPES } from '../../valuePickerUtils';
import { contentTypes, ACCESS_LEVELS } from '../../../../../../constants';
import ValueEditorPrimitive from './ValueEditorPrimitive';
import ValueEditorComplex from './ValueEditorComplex';
import Breadcrumb from '../../../../../Breadcrumb';
import '../../../../../../../css/value-editor.less';
import classnames from 'classnames';

/**
 * The Value Editor
 *
 * Presents a Modal to allow the creation or update of a single Value.
 *
 * A value can be one of three basic types, 'primitive' (a string/number/...) or a 'complex' type
 * that is an object with properties. This complex type can be a single instance 'Type' or a
 * collection of types 'List'.
 *
 * A List and a Type are both treated the same with the only distinction is that
 * a Type is essentially a List with only ONE entry.
 *
 * For more details on managing this data see the comments in the reducer.
 *
 * @param {Object} value the complete Value object
 * @param {Array} typeElements list of all known types
 * @param {Function} onSubmit submission handler
 * @param {Function} switchView switch back to the Value Picker
 * @param {Function} handleDeveloperName update Value's name
 * @param {Function} handleContentType update Value's content type
 * @param {Function} handleTypeElementId update Value's Type Element Id
 * @param {Function} handleContentFormat update Value's content format
 * @param {Function} handleIsFixed update Value's is fixed attribute
 * @param {Function} handleAccess update Value's access attribute
 * @param {Function} handleIsVersionless update Value's is versionless attribute
 * @param {Function} handleDeveloperSummary update Value's summary information
 * @param {Array} breadcrumbTrail trail of breadcrumb objects identifying the nested values by id
 * @param {String} breadcrumbActiveId the Id of the active value in the trail
 */
export const ValueEditor = ({ value, typeElements, onSubmit, switchView,
    handleDeveloperName,
    handleContentType,
    handleTypeElementId,
    handleContentFormat,
    handleIsFixed,
    handleAccess,
    handleIsVersionless,
    handleDeveloperSummary,
    breadcrumbTrail,
    breadcrumbActiveId,
}) => {
    const currentValueType = VALUE_TYPES.find(valueType => valueType.value === value.contentType);
    const canBeFormatted = currentValueType ? currentValueType.canBeFormatted : false;
    const requiresType = currentValueType ? currentValueType.requiresType : false;
    const typeElementId = currentValueType ? currentValueType.typeElementId : false;
    const isPrimitive = currentValueType ? !requiresType : false;
    return (
        <form className="flex-column" onSubmit={onSubmit}>
            <Modal.Header>
                <h4 className="modal-title">
                    {value.id ? 'Edit Value' : 'Create new Value'}
                </h4>
            </Modal.Header>
            <Modal.Body>
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-md-6">
                            <div className="developer-name">
                                <GenericFormField
                                    value={value.developerName}
                                    onChange={e => handleDeveloperName(e.target)}
                                    label="Name"
                                    placeholder="Enter a name for this value"
                                    isRequired
                                />
                            </div>
                        </div>
                        <div className="col-md-6">
                            <div className="content-type form-group">
                                <label>
                                    What kind of Value is this?
                                    <span className="input-required"> *</span>
                                </label>
                                <select
                                    value={currentValueType ? currentValueType.value : null}
                                    onChange={e => handleContentType(e.target)}
                                    placeholder="What kind of Value is this?"
                                    className={`form-control ${(currentValueType == null) ? 'select-nothing-selected' : ''}`}
                                    required
                                >
                                    <option disabled hidden selected>What kind of Value is this?</option>
                                    {VALUE_TYPES.map(item => (
                                        <option key={item.value} value={item.value}>
                                            {item.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            {
                                // For a complex type determine if a List or Object
                                requiresType ? (
                                    <div className="type-element-id form-group">
                                        <label>
                                            What Type of Value is this?
                                            <span className="input-required"> *</span>
                                        </label>
                                        <select
                                            value={value.typeElementId}
                                            onChange={e => handleTypeElementId(e.target)}
                                            placeholder="What Type of Value is this?"
                                            className="form-control"
                                            required
                                        >
                                            <option disabled hidden selected />
                                            {typeElements.map(item => (
                                                <option key={item.id} value={item.id}>
                                                    {item.developerName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                ) : null
                            }
                        </div>
                        <div className="col-md-12">
                            <div className="changes">
                                <div className="form-group">
                                    <label>
                                        Access
                                        <span className="input-required"> *</span>
                                    </label>
                                    <select
                                        value={value.access}
                                        onChange={e => handleAccess(e.target)}
                                        placeholder="Access level"
                                        className="form-control"
                                        required
                                        disabled={!value.contentType}
                                    >
                                        {Object.values(ACCESS_LEVELS).map(item => (
                                            <option key={item.value} value={item.value}>
                                                {item.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <GenericFormField
                                    value={value.isFixed}
                                    type={contentTypes.boolean}
                                    onChange={e => handleIsFixed(e.target)}
                                    label="Do not allow changes to this Value"
                                    isRequired={false}
                                    disabled={!value.contentType}
                                />

                                <GenericFormField
                                    value={value.isVersionless}
                                    type={contentTypes.boolean}
                                    onChange={e => handleIsVersionless(e.target)}
                                    label="The Value should not be versioned"
                                    isRequired={false}
                                    disabled={!value.contentType}
                                />
                            </div>
                        </div>
                    </div>

                    {
                        isPrimitive ? (
                            <div
                                className={classnames('row default-value', canBeFormatted ? 'col-md-6' : 'col-md-12')}
                            >
                                <h3>Default Value</h3>
                                <ValueEditorPrimitive value={value} />
                            </div>
                        ) : null
                    }

                    {
                        // Allow entry of Default Values for a Complex type once the Type has been selected.
                        value.typeElementId && requiresType ? (
                            <React.Fragment>
                                <div className="row col-md-12 default-value">
                                    <h3>Default Value</h3>
                                </div>
                                <div className="row col-md-12 breadcrumbs form-group">
                                    <Breadcrumb trail={breadcrumbTrail} activeItemId={breadcrumbActiveId} />
                                </div>
                                <ValueEditorComplex
                                    contentType={value.contentType}
                                    valueEditorId={breadcrumbActiveId}
                                />
                            </React.Fragment>
                        ) : null
                    }

                    {
                        canBeFormatted ? (
                            <div className="row col-md-6 presentation">
                                <h3>Presentation</h3>
                                <GenericFormField
                                    value={value.contentFormat || ''}
                                    onChange={e => handleContentFormat(e.target)}
                                    label="The format that should be applied to this Value"
                                    isRequired={false}
                                    placeholder="Enter a output format specification"
                                />
                            </div>
                        ) : null
                    }

                    <div className="row col-md-12 comments">
                        <GenericFormField
                            value={value.developerSummary || ''}
                            type={contentTypes.content}
                            onChange={e => handleDeveloperSummary(e.target)}
                            label="Comments about this Value"
                            placeholder="Enter any supporting comments for this Value"
                            isRequired={false}
                            disabled={!value.contentType}
                        />
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                <button className="outcome btn btn-default btn-outline" onClick={() => switchView(ELEMENT_LIST_VIEW)}>Cancel</button>
                <button
                    className="outcome btn btn-primary"
                    type="submit"
                    disabled={
                        !(
                            // Must have a developerName
                            value.developerName &&
                            // Must have a contentType
                            value.contentType &&
                            (
                                // If it requires a type, a type must be chosen 
                                (requiresType && typeElementId) ||
                                // But if the value type is primitive, then that's fine
                                isPrimitive
                            )
                        )
                    }
                >
                Save
                </button>
            </Modal.Footer>
        </form>
    );
};

const mapDispatchToProps = {
    handleDeveloperName: setDeveloperName,
    handleContentType: setContentType,
    handleTypeElementId: setTypeElementId,
    handleContentFormat: setContentFormat,
    handleIsFixed: setIsFixed,
    handleAccess: setAccess,
    handleIsVersionless: setIsVersionless,
    handleDeveloperSummary: setDeveloperSummary,
};

// Exported for testing only
export const mapStateToProps =
    ({ valueEditor: { value, breadcrumbTrail, breadcrumbActiveId }, typeElements }) => ({ value, typeElements, breadcrumbTrail, breadcrumbActiveId });

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(ValueEditor);
